// Third-party
import { useRef, useState } from 'react';

// First-party
import { If, createPage, For, IntrinsicProps } from '@/common/util/templateHelpers';
import { Meta } from '@/common/components/head';
import { Button } from '@/common/components/controls';
import { Block, Box, Container, Content, Flex, Title } from '@/common/components';

// Component lib
import { PlaylistBasic } from '@/util/formatters';
import NoPaddingLayout from '@/layouts/NoPaddingLayout';
import BlockContainer from '@/components/BlockContainer';
import PlaylistTile from '@/components/PlaylistTile';
import playlistApi, { PLAYLIST_LIMIT } from '@/apis/drupal/playlist';
import { useNotifications } from '@/common/hooks/notifications';

// Local
import style from './index.module.scss';
import { generateBreadcrumbListLDJSON, generatePlaylistListLDJSON, generateWebsiteLDJSON } from '@/common/components/head/Meta';
import { useRouter } from 'next/dist/client/router';
import { logErrorEvent } from '@/common/util/logEvent';

interface PlaylistsPageProps extends IntrinsicProps {
  playlists: PlaylistBasic[];
}

function notFound (path: string, error: any) {
  logErrorEvent(`Playlist List Page Error - ${path}`, true, error);

  return {
    notFound: true,
    revalidate: process.env.APP_CONFIG.REVALIDATE_INTERVAL_MEDIUM
  };
}

export async function getStaticProps () {
  const asPath = '/playlists';

  const playlists = await playlistApi.listAll();
  if (playlists.error) return notFound(asPath, new Error('Could not fetch playlists.'));

  return {
    props: {
      playlists
    },
    revalidate: process.env.APP_CONFIG.REVALIDATE_INTERVAL_MEDIUM
  };
}

export default createPage<PlaylistsPageProps>('PlaylistsPage',
  { style },
  function PlaylistsPage ({ className }, props) {
    const router = useRouter();
    const playlistOffsetRef = useRef(props.playlists?.length ?? 0);
    const [ playlists, setPlaylists ] = useState(props.playlists ?? []);
    const [ isLoading, setIsLoading ] = useState(false);
    const [ isNoMorePlaylists, setIsNoMorePlaylists ] = useState(false);

    const { notify } = useNotifications();

    const fetchNextPlaylists = async () => {
      return await playlistApi.listAll(PLAYLIST_LIMIT, playlistOffsetRef.current);
    };

    const fetchMorePlaylists = async () => {
      setIsLoading(true);

      const nextPlaylists = await fetchNextPlaylists();
      if (nextPlaylists.error) {
        setIsLoading(false);
        return notify('An error has occurred, please try again later.', 'error');
      }

      playlistOffsetRef.current += nextPlaylists.length;

      setPlaylists([
        ...playlists,
        ...nextPlaylists
      ]);

      if (nextPlaylists.length < PLAYLIST_LIMIT) {
        setIsNoMorePlaylists(true);
      }

      setIsLoading(false);
    };
    
    return (
      <NoPaddingLayout className={className}>
        <Meta 
          title='Playlists' 
          titleTag='Playlists | Addicting Games'
          ldjson={[ 
            ...generateWebsiteLDJSON(router.asPath),
            ...generateBreadcrumbListLDJSON([
              { name: 'Playlists', url: router.asPath, }
            ]),
            ...generatePlaylistListLDJSON(process.env.APP_CONFIG.URL + '/playlist/', [ ...playlists ]),
          ]} 
          sitemapProps={{ priority: '0.6' }}
        />

        <Container>
          <BlockContainer>
            <Block>
              <Title h1>Playlists</Title>
            </Block>
            <Block>
              <Box>
                <Content>
                  <p>
                    You've traded tapes, CD's, and streaming service playlists with
                    your friends for years now, so, why not do the same thing with
                    video games? We really put the playback in playlist with our
                    carefully curated lists of hand-picked video games. That's
                    right! We've hunkered down, sifted through our library of
                    thousands of games, and produced a series of lists that we feel
                    represent the best games we have to offer. We just know you'll
                    want to play your way through these lists as we pull from a
                    variety of themes and category options on our site. Whether
                    you're looking for ninjas, tanks, space, war or sports games
                    you can rest assured knowing that we have a perfectly manicured
                    playlist that highlights the stand out games of that particular
                    genre.
                  </p>
                  <p>
                    Our playlists are designed for people who are looking for
                    themed gaming sessions of all kinds. Sometimes that might mean
                    you'll want to sit down and play all of our best Halloween
                    games, our best Ninja games, our best puzzle games or just the
                    most original games on the site, we understand. We know what
                    it's like to want to play some awesome free games online and
                    have to click through endless categories looking for just the
                    right game to waste the day. So, we put the work in so you
                    don't have to! Please look around and be confident that we have
                    only your best interests in mind. Please, be our guest, get
                    comfortable, click around and play the rest of your day away!
                  </p>
                </Content>
              </Box>
            </Block>
          </BlockContainer>
        </Container>

        <Container>
          <BlockContainer>
            <Block>
              {
                If(playlists.length > 0, () => (
                  <Flex wrap alignCenter justifyCenter className='PlaylistsPage__Playlists'>
                    {
                      For(playlists, (playlist) => (
                        <PlaylistTile playlist={playlist} key={playlist.nid} />
                      ))
                    }
                  </Flex>
                ))
                  .Else(() => (
                    <Flex alignCenter justifyCenter center>
                      <span className='PlaylistsPage__NoPlaylists'>
                        No playlists found.
                      </span>
                    </Flex>
                  ))
                  .EndIf()
              }
            </Block>
            <Block>
              <Flex alignCenter justifyCenter>
                {
                  If(!isNoMorePlaylists, () => (
                    <Button loading={isLoading} primary onClick={fetchMorePlaylists}>More Playlists!</Button>
                  ))
                    .EndIf()
                }
              </Flex>
            </Block>
          </BlockContainer>
        </Container>

      </NoPaddingLayout>
    );
  });
